import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Title } from '@xsolla/ui-core';
import { Modal } from '@xsolla/ui-modal';

import { getMailchimpMergeTags, postMailchimpMergeTags } from '../../../../api';
import { MergeTag } from '../../../../api/users/mailchimp/types';
import { useNotifications } from '../../../../hooks/useNotifications';
import { CollapseWithTitle, LinkPAStyle } from '../../../ui';
import FormComponent from './FormComponent';

type Props = {
  onClose: () => void;
  onSubmit: () => void;
  projectId: string;
  mailchimpListId: string;
};

const ConnectModal = ({ onClose, onSubmit, projectId, mailchimpListId }: Props): JSX.Element => {
  const intl = useIntl();
  const { showErrorNotify, showSuccessNotify } = useNotifications();

  const [data, setData] = useState<MergeTag[]>([
    { field_to_export: '', mailchimp_field_label: '', mailchimp_merge_tag: '' }
  ]);
  const [hasData, setHasData] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    getMailchimpMergeTags(projectId)
      .then((res) => {
        if (res.length === 0) {
          setData([{ field_to_export: '', mailchimp_field_label: '', mailchimp_merge_tag: '' }]);
        } else {
          setData(res);
        }
      })
      .catch((err) => {
        showErrorNotify(err);
      })
      .finally(() => setFetching(false));
  }, []);

  useEffect(() => {
    if (
      data[0]?.field_to_export === '' &&
      data[0]?.mailchimp_field_label === '' &&
      data[0]?.mailchimp_merge_tag === ''
    ) {
      setHasData(false);
    } else {
      setHasData(true);
    }
  }, [data]);

  const addRow = () => {
    setData([...data, { field_to_export: '', mailchimp_field_label: '', mailchimp_merge_tag: '' }]);
  };

  const deleteRow = (index: number) => {
    const item = data[index];
    if (
      data.length === 1 &&
      item.field_to_export === '' &&
      item.mailchimp_field_label === '' &&
      item.mailchimp_merge_tag === ''
    ) {
      return;
    }
    const updatedData = data.filter((_, i) => i !== index);
    setData(updatedData);
  };

  const handleChange = (index: number, field: keyof MergeTag, value: string) => {
    const updatedData = [...data];
    updatedData[index][field] = value;
    setData(updatedData);
  };

  const submitData = async () => {
    setFetching(true);
    postMailchimpMergeTags(projectId, mailchimpListId, data)
      .then(() => {
        showSuccessNotify();
        onSubmit();
      })
      .catch((err) => {
        showErrorNotify(err);
        setError(true);
      })
      .finally(() => setFetching(false));
  };

  return (
    <Modal size="lg">
      <Title>{intl.formatMessage({ id: 'users.mailchimp.merge-tags-form.head' })}</Title>
      <CollapseWithTitle
        title={intl.messages['users.mailchimp.notification.title']}
        instruction={intl.formatMessage(
          {
            id: 'users.mailchimp.notification.description.mailchimp-merge-tags'
          },
          {
            br: () => <br />,
            b: (chunk: string) => <b>{chunk}</b>,
            a1: (chunk: string) => (
              <LinkPAStyle target="_blank" rel="noreferrer">
                {chunk}
              </LinkPAStyle>
            ),
            a2: (chunk: string) => (
              <LinkPAStyle target="_blank" rel="noreferrer">
                {chunk}
              </LinkPAStyle>
            ),
            a3: (chunk: string) => (
              <LinkPAStyle target="_blank" rel="noreferrer">
                {chunk}
              </LinkPAStyle>
            )
          }
        )}
      />
      <FormComponent
        data={data}
        handleChange={handleChange}
        deleteRow={deleteRow}
        addRow={addRow}
        onSubmit={submitData}
        onClose={onClose}
        hasData={hasData}
        fetching={fetching}
        error={error}
        submitText={intl.formatMessage({
          id: hasData
            ? 'users.mailchimp.confirm-form.button'
            : 'users.mailchimp.merge-tags-form.button.skip'
        })}
      />
    </Modal>
  );
};

export default ConnectModal;
